import { useEffect, useRef } from 'react'
import { useStore } from '@/store'
import { useAnalysisDispatch } from './hooks'
import { fetchSearch } from './searchSlice'
import { buildQuery } from './queryBuilder'
import type { Query } from './apiTypes'
import { API_BASE_URL, WS_BASE_URL } from './endpoints'
import { useAuthStore } from '@/auth/authStore'

const NON_ANALYTICS = new Set(['incidents', 'reports', 'settings', 'users'])

function stableStringify(obj: unknown) {
  const seen = new WeakSet<object>()
  const norm = (v: unknown): unknown => {
    if (!v || typeof v !== 'object') return v
    if (seen.has(v as object)) return null
    seen.add(v as object)
    if (Array.isArray(v)) return v.map(norm)
    const src = v as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(src).sort()) out[k] = norm(src[k])
    return out
  }
  return JSON.stringify(norm(obj))
}

/**
 * Owns the data lifecycle for analytics views (world / hex / deep).
 * Non-analytics views skip REST/WS so sidebar switches do not spam the API.
 */
export default function DataController() {
  const dispatch = useAnalysisDispatch()

  const dimension = useStore((s) => s.dimension)
  const comparison = useStore((s) => s.comparison)

  const debRef = useRef<number | null>(null)
  const lastKeyRef = useRef<string>('')
  const didInitialRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    console.info('[data] controller mount', { API_BASE_URL, WS_BASE_URL })

    const fire = (state: ReturnType<typeof useStore.getState>, query: Query, key: string) => {
      console.info('[data] fire', {
        view: query.view,
        dimension: query.dimension,
        slice: query.slice,
        streamId: query.filters.streamId,
      })

      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac

      const isHexDrill = state.view === 'hex' && !!state.drillNode

      // Soft-hold: pause WS merges but keep the previous paint frame until REST lands.
      useStore.setState({
        dataDebouncing: false,
        streamsLoading: true,
        wsUiPaused: true,
        activeDataKey: key,
        restReadyKey: null,
        wsSubscribedKey: null,
        lastDataError: null,
        lastStreamsUpdateTs: null,
      })

      dispatch({ type: 'ws/resubscribe', payload: { query } })

      void dispatch(fetchSearch({ key, query, scope: isHexDrill ? 'drill' : 'list' }, { signal: ac.signal }))
        .unwrap()
        .then(() => {
          if (ac.signal.aborted) return
          // REST landed. If WS subscribe ack is delayed/missing (e.g. world),
          // still release the paint gate so the map is not stuck empty.
          const st = useStore.getState()
          if (st.activeDataKey === key && st.restReadyKey === key) {
            useStore.setState({
              streamsLoading: false,
              wsUiPaused: false,
              ...(st.wsSubscribedKey !== key ? { wsSubscribedKey: key } : {}),
            })
          }
        })
        .catch((e: unknown) => {
          if (ac.signal.aborted) return
          useStore.setState({
            lastDataError: String((e as Error)?.message ?? e),
            streamsLoading: false,
            wsUiPaused: false,
          })
        })
    }

    const tick = () => {
      const state = useStore.getState()

      if (NON_ANALYTICS.has(state.view)) {
        if (lastKeyRef.current !== `idle:${state.view}`) {
          lastKeyRef.current = `idle:${state.view}`
          abortRef.current?.abort()
          useStore.setState({
            dataDebouncing: false,
            streamsLoading: false,
            wsUiPaused: false,
            activeDataKey: null,
            restReadyKey: null,
            wsSubscribedKey: null,
          })
        }
        return
      }

      const query = buildQuery({
        view: state.view,
        dimension: state.dimension,
        drillNode: state.drillNode,
        drillStream: state.drillStream,
        filters: state.filters,
        timeMode: state.timeMode,
        window: state.window,
        historicTs: state.historicTs,
        historicFromTs: state.historicFromTs,
        clientGrain: state.worldClientGrain,
        includeInactive: state.includeInactive,
      })

      const ctxKey = stableStringify({
        query,
        view: state.view,
        drillNode: state.drillNode ?? null,
      })
      if (ctxKey === lastKeyRef.current) return
      lastKeyRef.current = ctxKey

      const queryKey = stableStringify(query)
      if (!didInitialRef.current) {
        didInitialRef.current = true
        fire(state, query, queryKey)
        return
      }

      useStore.setState({ dataDebouncing: true })

      if (debRef.current) window.clearTimeout(debRef.current)
      const expected = ctxKey
      debRef.current = window.setTimeout(() => {
        if (lastKeyRef.current !== expected) return
        fire(useStore.getState(), query, queryKey)
      }, 220)
    }

    const unsubStore = useStore.subscribe(tick)
    dispatch({ type: 'ws/connect' })
    queueMicrotask(tick)

    let prevToken = useAuthStore.getState().accessToken
    const unsubAuth = useAuthStore.subscribe((state) => {
      const nextToken = state.accessToken
      if (nextToken === prevToken) return
      prevToken = nextToken
      if (!nextToken) return
      dispatch({ type: 'ws/disconnect' })
      dispatch({ type: 'ws/connect' })
    })

    return () => {
      if (debRef.current) window.clearTimeout(debRef.current)
      abortRef.current?.abort()
      unsubStore()
      unsubAuth()
    }
  }, [dispatch])

  void dimension
  void comparison

  return null
}
